/**
 * Storefront Configuration
 * 
 * This file manages the configuration for different storefronts that can
 * use the dynamic content delivery system (ads, recommendations, video carousel).
 * 
 * SWITCHING STORES:
 * 1. Update ACTIVE_STORE to point to the desired store key
 * 2. Add new store configurations in STORE_CONFIGS as needed
 * 3. The hooks (AdHook, RecommendationHook, etc.) will automatically use the active config
 * 
 * SUPPORTED CHANNELS:
 * - shopify: Shopify storefront (no API key required)
 * - own_website: Custom React/Next.js website (API key required)
 * - mobile_app: React Native/Flutter mobile app (API key required)
 */

// ==================== ACTIVE STORE SETTING ====================
// Change this to switch between different store configurations
export const ACTIVE_STORE = 'test_store';

// ==================== STORE CONFIGURATIONS ====================
export const STORE_CONFIGS = {
  // Test store for development and testing
  test_store: {
    id: 'test_store',
    name: 'Test Storefront',
    channel: 'own_website',
    apiKey: null,  // Will use default (no auth for testing)
    settings: {
      showSponsoredBadge: true,
      trackingEnabled: true,
      debugMode: true,
      fallbackEnabled: true
    },
    slots: {
      homeBanner: 'HOME_BANNER_01',
      homeVideoCarousel: 'HOME_VIDEO_CAROUSEL_01',
      categoryBanner: 'CATEGORY_TOP_01',
      searchSidebar: 'SEARCH_SIDEBAR_01',
      productRecommendations: 'PRODUCT_RECS_01'
    },
    theme: {
      sponsoredBadgeColor: 'bg-amber-500',
      sponsoredBadgeText: 'Sponsored',
      primaryColor: 'blue'
    }
  },
  
  // Production Shopify store
  shopify_production: {
    id: 'shopify_production',
    name: 'Main Shopify Store',
    channel: 'shopify',
    apiKey: null,  // Shopify uses session auth
    settings: {
      showSponsoredBadge: true,
      trackingEnabled: true,
      debugMode: false,
      fallbackEnabled: true
    },
    slots: {
      homeBanner: 'SHOPIFY_HOME_BANNER',
      homeVideoCarousel: 'SHOPIFY_VIDEO_01',
      categoryBanner: 'SHOPIFY_CAT_BANNER',
      searchSidebar: 'SHOPIFY_SEARCH',
      productRecommendations: 'SHOPIFY_RECS'
    },
    theme: {
      sponsoredBadgeColor: 'bg-green-600',
      sponsoredBadgeText: 'Ad',
      primaryColor: 'green'
    }
  },
  
  // Mobile app configuration
  mobile_app: {
    id: 'mobile_app',
    name: 'Mobile App',
    channel: 'mobile_app',
    apiKey: process.env.REACT_APP_MOBILE_API_KEY || null,
    settings: {
      showSponsoredBadge: true,
      trackingEnabled: true,
      debugMode: false,
      fallbackEnabled: true
    },
    slots: {
      homeBanner: 'MOBILE_HOME_BANNER',
      homeVideoCarousel: 'MOBILE_VIDEO_01',
      categoryBanner: 'MOBILE_CAT_BANNER',
      searchSidebar: null,  // Not applicable for mobile
      productRecommendations: 'MOBILE_RECS'
    },
    theme: {
      sponsoredBadgeColor: 'bg-purple-600',
      sponsoredBadgeText: 'Sponsored',
      primaryColor: 'purple'
    }
  },
  
  // Custom website template
  custom_website: {
    id: 'custom_website',
    name: 'Custom Website',
    channel: 'own_website',
    apiKey: process.env.REACT_APP_CUSTOM_SITE_API_KEY || null,
    settings: {
      showSponsoredBadge: true,
      trackingEnabled: true,
      debugMode: false,
      fallbackEnabled: true
    },
    slots: {
      homeBanner: 'CUSTOM_HOME_BANNER',
      homeVideoCarousel: 'CUSTOM_VIDEO_01',
      categoryBanner: 'CUSTOM_CAT_BANNER',
      searchSidebar: 'CUSTOM_SEARCH',
      productRecommendations: 'CUSTOM_RECS'
    },
    theme: {
      sponsoredBadgeColor: 'bg-blue-600',
      sponsoredBadgeText: 'Sponsored',
      primaryColor: 'blue'
    }
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get the active store configuration
 * @returns {Object} The active store config
 */
export const getActiveStoreConfig = () => {
  return STORE_CONFIGS[ACTIVE_STORE] || STORE_CONFIGS.test_store;
};

/**
 * Get a specific store configuration by ID
 * @param {string} storeId - The store ID
 * @returns {Object|null} The store config or null
 */
export const getStoreConfig = (storeId) => {
  return STORE_CONFIGS[storeId] || null;
};

/**
 * Get all available store configurations
 * @returns {Array} Array of store configs
 */
export const getAllStoreConfigs = () => {
  return Object.values(STORE_CONFIGS);
};

/**
 * Get the slot ID for a specific slot type in the active store
 * @param {string} slotType - The slot type (e.g., 'homeBanner', 'categoryBanner')
 * @returns {string|null} The slot ID or null
 */
export const getSlotId = (slotType) => {
  const config = getActiveStoreConfig();
  return config.slots?.[slotType] || null;
};

/**
 * Get the channel type for the active store
 * @returns {string} The channel type
 */
export const getActiveChannel = () => {
  const config = getActiveStoreConfig();
  return config.channel || 'own_website';
};

/**
 * Get the API key for the active store
 * @returns {string|null} The API key or null
 */
export const getActiveApiKey = () => {
  const config = getActiveStoreConfig();
  return config.apiKey || null;
};

/**
 * Check if debug mode is enabled for the active store
 * @returns {boolean} Whether debug mode is enabled
 */
export const isDebugMode = () => {
  const config = getActiveStoreConfig();
  return config.settings?.debugMode || false;
};

/**
 * Check if tracking is enabled for the active store
 * @returns {boolean} Whether tracking is enabled
 */
export const isTrackingEnabled = () => {
  const config = getActiveStoreConfig();
  return config.settings?.trackingEnabled !== false;
};

/**
 * Get theme configuration for the active store
 * @returns {Object} Theme configuration
 */
export const getThemeConfig = () => {
  const config = getActiveStoreConfig();
  return config.theme || {
    sponsoredBadgeColor: 'bg-amber-500',
    sponsoredBadgeText: 'Sponsored',
    primaryColor: 'blue'
  };
};

// ==================== VIDEO CAROUSEL CONFIG ====================
export const VIDEO_CAROUSEL_CONFIG = {
  // 1:3 ratio - 1 auction slot, 3 organic slots
  auctionPositions: [0],
  organicPositions: [1, 2, 3],
  totalSlots: 4,
  videoSpecs: {
    formats: ['mp4', 'webm'],
    aspectRatio: '4:5',
    maxDurationSeconds: 15,
    maxFileSizeMB: 50
  }
};

// ==================== DEFAULT SLOT POSITIONS ====================
export const DEFAULT_SLOT_POSITIONS = {
  homepage: [0, 4, 8],       // Sponsored at positions 0, 4, 8
  category: [0, 3, 6],       // Sponsored at positions 0, 3, 6
  search: [2, 7, 12],        // Sponsored at positions 2, 7, 12
  product_detail: [1],       // One sponsored slot
  video_carousel: [0]        // First slot is auction
};

export default {
  ACTIVE_STORE,
  STORE_CONFIGS,
  VIDEO_CAROUSEL_CONFIG,
  DEFAULT_SLOT_POSITIONS,
  getActiveStoreConfig,
  getStoreConfig,
  getAllStoreConfigs,
  getSlotId,
  getActiveChannel,
  getActiveApiKey,
  isDebugMode,
  isTrackingEnabled,
  getThemeConfig
};
